import type { User, UserRole } from '../types';
import { api, clearAuthSession, getToken, refreshSession, setToken, setRefreshToken } from '../lib/api';
import { AUTH_PERMISSIONS_KEY, AUTH_ROLE_KEY, AUTH_USER_KEY } from '../lib/authStorage';

/** Backend role keys → the UI's richer UserRole set. */
const ROLE_MAP: Record<string, UserRole> = {
  admin: 'admin',
  frontdesk: 'frontdesk',
  housekeeping: 'housekeeping',
  finance: 'finance',
  manager: 'manager',
  super_admin: 'super_admin',
  property_admin: 'property_admin',
};

/** Decode a JWT payload (no verification — just to read sub/roleKey for the UI). */
function decodeJwt(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    ) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function getTokenExpiryMs(token: string): number | null {
  const payload = decodeJwt(token);
  const exp = payload.exp;
  return typeof exp === 'number' ? exp * 1000 : null;
}

interface LoginResponse {
  accessToken?: string;
  refreshToken?: string;
  requiresTwoFactor?: boolean;
  requiresApproval?: boolean;
  candidates?: number[];
  ticket?: string;
  mustSetup2fa?: boolean;
}

export interface TwoFactorSetup {
  secret: string;
  qrCodeDataUrl: string;
}

interface BackendUser {
  id: string;
  username: string | null;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  isActive: boolean;
  totpEnabled: boolean;
  lastLoginAt: string | null;
  role?: { key: string };
  /** The account's permanent role; differs from role only during a role preview. */
  baseRoleKey?: string;
  /** Present on /auth/me only — the caller's own resolved grants. */
  permissions?: string[];
}

const mapBackendUser = (user: BackendUser): User => ({
  id: user.id,
  username: user.username ?? user.email.split('@')[0],
  email: user.email,
  role: ROLE_MAP[user.role?.key ?? ''] ?? 'frontdesk',
  firstName: user.firstName,
  lastName: user.lastName,
  phone: user.phone ?? undefined,
  isActive: user.isActive,
  lastLogin: user.lastLoginAt ? new Date(user.lastLoginAt) : undefined,
  totpEnabled: user.totpEnabled,
});

/** Thrown when the backend requires a TOTP step; carries the ticket that binds the 2FA to this attempt. */
export class TwoFactorRequiredError extends Error {
  constructor(public readonly ticket: string) {
    super('Two-factor authentication required');
    this.name = 'TwoFactorRequiredError';
  }
}

/** Thrown for super_admin sign-ins after a valid TOTP code — one more out-of-band pick before a session is issued. */
export class ApprovalRequiredError extends Error {
  constructor(public readonly ticket: string, public readonly candidates: number[]) {
    super('Sign-in approval required');
    this.name = 'ApprovalRequiredError';
  }
}

export class AuthService {
  private static instance: AuthService;
  private currentUser: User | null = null;
  /** Signed permanent role, retained while a Super Administrator previews another role. */
  private baseRole: UserRole | null = null;
  private authenticatedRole: UserRole | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private mustSetup2fa = false;
  private permissions: string[] = [];

  private constructor() {
    this.loadUserFromStorage();
    this.loadAuthenticatedRole();
    this.loadPermissions();
    this.bootstrapSessionValidity();
  }

  private loadPermissions(): void {
    try {
      const stored = localStorage.getItem(AUTH_PERMISSIONS_KEY);
      this.permissions = stored ? (JSON.parse(stored) as string[]) : [];
    } catch {
      this.permissions = [];
    }
  }

  /** What the backend actually granted this account for its effective role. */
  getPermissions(): string[] {
    return this.permissions;
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private loadUserFromStorage(): void {
    try {
      const stored = localStorage.getItem(AUTH_USER_KEY);
      if (stored) {
        this.currentUser = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading user from storage:', error);
      localStorage.removeItem(AUTH_USER_KEY);
    }
  }

  private saveUserToStorage(user: User): void {
    try {
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('Error saving user to storage:', error);
    }
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  private expireSession(reason: string = 'session-expired'): void {
    this.clearExpiryTimer();
    this.currentUser = null;
    this.baseRole = null;
    this.authenticatedRole = null;
    this.permissions = [];
    clearAuthSession();
    window.dispatchEvent(new CustomEvent('auth:expired', { detail: { reason } }));
  }

  /**
   * Fired shortly before the access token expires. Silently swap the refresh
   * token for a fresh access token and reschedule; only end the session if the
   * refresh token itself is gone/expired — never mid-activity on a live session.
   */
  private async refreshOrExpire(): Promise<void> {
    const refreshed = await refreshSession();
    if (refreshed) this.bootstrapSessionValidity();
    else this.expireSession();
  }

  private scheduleRefresh(msUntilExpiry: number): void {
    this.clearExpiryTimer();
    const lead = 30_000;
    const delay = msUntilExpiry > lead ? msUntilExpiry - lead : Math.max(0, msUntilExpiry);
    this.expiryTimer = setTimeout(() => {
      void this.refreshOrExpire();
    }, delay);
  }

  private bootstrapSessionValidity(): void {
    const token = getToken();

    if (!token) {
      if (this.currentUser || this.authenticatedRole) {
        this.currentUser = null;
        this.baseRole = null;
        this.authenticatedRole = null;
        this.permissions = [];
        clearAuthSession();
      }
      return;
    }

    const expiryMs = getTokenExpiryMs(token);
    if (!expiryMs) return;

    const msUntilExpiry = expiryMs - Date.now();
    if (msUntilExpiry <= 0) {
      void this.refreshOrExpire();
      return;
    }

    this.scheduleRefresh(msUntilExpiry);
  }

  validateSession(): boolean {
    const token = getToken();
    if (!token) {
      if (this.currentUser || this.authenticatedRole) {
        this.expireSession();
      }
      return false;
    }

    const expiryMs = getTokenExpiryMs(token);
    if (!expiryMs) {
      this.clearExpiryTimer();
      return true;
    }

    const msUntilExpiry = expiryMs - Date.now();
    if (msUntilExpiry <= 0) {
      void this.refreshOrExpire();
      return true;
    }

    this.scheduleRefresh(msUntilExpiry);
    return true;
  }

  /**
   * The signed token is the only trustworthy source for the session's role.
   * localStorage is user-writable, so reading it first meant typing
   * `hotel_authenticated_role = 'super_admin'` into devtools unlocked the
   * privileged UI — the API still refused the data, but the menus and routes
   * opened. Storage is now only a fallback for when no token is present.
   */
  private loadAuthenticatedRole(): void {
    const token = getToken();
    const tokenRole = token ? ROLE_MAP[String(decodeJwt(token).roleKey ?? '')] : undefined;
    const tokenBaseRole = token
      ? ROLE_MAP[String(decodeJwt(token).baseRoleKey ?? decodeJwt(token).roleKey ?? '')]
      : undefined;
    if (tokenRole) {
      this.authenticatedRole = tokenRole;
      this.baseRole = tokenBaseRole ?? tokenRole;
      localStorage.setItem(AUTH_ROLE_KEY, tokenRole);
      return;
    }

    const storedRole = localStorage.getItem(AUTH_ROLE_KEY) as UserRole | null;
    this.authenticatedRole =
      (storedRole && Object.values(ROLE_MAP).includes(storedRole) ? storedRole : null) ??
      this.currentUser?.role ??
      null;
    this.baseRole = this.authenticatedRole;
  }

  /**
   * Authentication is the backend's job. There is no client-side password list
   * and no offline/mock fallback — if the backend rejects the credentials or is
   * unreachable, the error propagates and the user sees it. A 2FA account throws
   * TwoFactorRequiredError so the caller can run the verify step.
   */
  async login(email: string, password: string): Promise<User> {
    setToken(null);
    const res = await api.post<LoginResponse>(
      '/auth/login',
      { email, password },
      { auth: false },
    );
    if (res?.requiresTwoFactor && res.ticket) {
      throw new TwoFactorRequiredError(res.ticket);
    }
    if (!res?.accessToken) {
      throw new Error('Login failed');
    }
    return this.establishSession(res.accessToken, res.refreshToken, res.mustSetup2fa);
  }

  /** Completes a 2FA login: the backend verifies the TOTP and only then issues the token (or, for super_admin, a further out-of-band approval). */
  async verifyTwoFactor(ticket: string, code: string): Promise<User> {
    const res = await api.post<LoginResponse>(
      '/auth/login/verify-2fa',
      { ticket, code },
      { auth: false },
    );
    if (res?.requiresApproval && res.ticket) {
      throw new ApprovalRequiredError(res.ticket, res.candidates ?? []);
    }
    if (!res?.accessToken) throw new Error('Two-factor verification failed');
    return this.establishSession(res.accessToken, res.refreshToken, res.mustSetup2fa);
  }

  /** Completes the out-of-band approval step (super_admin only) — the number the user picked must match the one emailed to them. */
  async verifyApproval(ticket: string, selectedNumber: number): Promise<User> {
    const res = await api.post<LoginResponse>(
      '/auth/login/verify-approval',
      { ticket, selectedNumber },
      { auth: false },
    );
    if (!res?.accessToken) throw new Error('Approval verification failed');
    return this.establishSession(res.accessToken, res.refreshToken, res.mustSetup2fa);
  }

  /** True right after login when mandatory 2FA enrollment hasn't been completed yet (super_admin only). Cleared once /auth/2fa/confirm succeeds. */
  needsTwoFactorSetup(): boolean {
    return this.mustSetup2fa;
  }

  /** `code` is only required when re-enrolling an account that already has 2FA on. */
  async beginTwoFactorSetup(password: string, code?: string): Promise<TwoFactorSetup> {
    return api.post<TwoFactorSetup>('/auth/2fa/setup', { password, code: code || undefined });
  }

  async confirmTwoFactorSetup(code: string): Promise<void> {
    const res = await api.post<LoginResponse & { success: boolean }>('/auth/2fa/confirm', { code });
    if (res?.accessToken) {
      await this.establishSession(res.accessToken, res.refreshToken, res.mustSetup2fa);
    }
  }

  async disableTwoFactor(password: string): Promise<{ success: boolean; message?: string }> {
    return api.post('/auth/2fa/disable', { password });
  }

  /**
   * Builds the client session from a backend-issued token. The JWT is decoded
   * only to read role/id for UI menu rendering — it is NOT trusted for
   * authorization; every API request is authorised server-side against the
   * token's verified claims.
   */
  private async establishSession(accessToken: string, refreshToken?: string, mustSetup2fa?: boolean): Promise<User> {
    this.clearExpiryTimer();
    setToken(accessToken);
    if (refreshToken !== undefined) setRefreshToken(refreshToken);
    this.mustSetup2fa = mustSetup2fa ?? false;
    const me = await api.get<BackendUser>('/auth/me');
    const user = mapBackendUser(me);
    this.currentUser = user;
    this.authenticatedRole = user.role;
    this.baseRole =
      ROLE_MAP[me.baseRoleKey ?? String(decodeJwt(accessToken).baseRoleKey ?? '')] ??
      user.role;
    this.permissions = me.permissions ?? [];
    localStorage.setItem(AUTH_ROLE_KEY, user.role);
    localStorage.setItem(AUTH_PERMISSIONS_KEY, JSON.stringify(this.permissions));
    this.saveUserToStorage(user);
    this.bootstrapSessionValidity();
    return user;
  }

  logout(): void {
    this.clearExpiryTimer();
    this.currentUser = null;
    this.baseRole = null;
    this.authenticatedRole = null;
    this.permissions = [];
    clearAuthSession();
  }

  getCurrentUser(): User | null {
    return this.currentUser;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  getAuthenticatedRole(): UserRole | null {
    return this.authenticatedRole;
  }

  canSwitchRoles(): boolean {
    return this.baseRole === 'super_admin';
  }

  async switchRole(newRole: UserRole): Promise<boolean> {
    if (!this.currentUser) return false;
    if (!this.canSwitchRoles()) {
      console.warn('Role switching is restricted to Super Administrators only.');
      return false;
    }
    const result = await api.post<{ accessToken?: string }>('/auth/role-switch', {
      roleKey: newRole,
    });
    if (!result.accessToken) return false;
    await this.establishSession(result.accessToken);
    window.dispatchEvent(new CustomEvent('roleChanged', { detail: { role: newRole } }));
    return true;
  }

  hasRole(role: UserRole | UserRole[]): boolean {
    if (!this.currentUser) return false;
    
    // Super Admin has access to everything
    if (this.currentUser.role === 'super_admin') return true;
    
    if (Array.isArray(role)) {
      return role.includes(this.currentUser.role);
    }
    
    return this.currentUser.role === role || this.currentUser.role === 'admin' || this.currentUser.role === 'property_admin';
  }

  async getAllUsers(): Promise<User[]> {
    return (await api.get<BackendUser[]>('/users')).map(mapBackendUser);
  }

  async getUserById(id: string): Promise<User | null> {
    return mapBackendUser(await api.get<BackendUser>(`/users/${id}`));
  }

  async createUser(userData: Omit<User, 'id' | 'lastLogin'>): Promise<User> {
    const created = await api.post<BackendUser>('/users', {
      username: userData.username,
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      phone: userData.phone,
      roleKey: userData.role,
      isActive: userData.isActive,
    });
    return mapBackendUser(created);
  }

  async updateUser(user: User): Promise<User> {
    const updated = mapBackendUser(await api.patch<BackendUser>(`/users/${user.id}`, {
      username: user.username,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone ?? '',
      roleKey: user.role,
      isActive: user.isActive,
      ...(user.password ? { password: user.password } : {}),
    }));
    if (this.currentUser?.id === updated.id) {
      this.currentUser = updated;
      this.saveUserToStorage(updated);
    }
    return updated;
  }

  async deleteUser(id: string): Promise<void> {
    await api.del(`/users/${id}`);
  }

  /** Who an invitation link belongs to — the token goes in the body, never the URL. */
  async verifyPasswordSetup(token: string): Promise<{ email: string; firstName: string }> {
    return api.post<{ email: string; firstName: string }>(
      '/auth/password-setup/verify',
      { token },
      { auth: false },
    );
  }

  async completePasswordSetup(token: string, password: string): Promise<void> {
    await api.post('/auth/password-setup/complete', { token, password }, { auth: false });
  }

  async requestPasswordReset(email: string): Promise<void> {
    await api.post('/auth/password-reset/request', { email }, { auth: false });
  }

  /**
   * Re-sends the onboarding link, invalidating any unused one. This is also
   * how a lost authenticator is recovered: completing the emailed link clears
   * the old second factor. There is deliberately no way to clear someone
   * else's two-factor directly.
   */
  async resendInvitation(id: string): Promise<void> {
    await api.post(`/users/${id}/resend-invitation`);
  }
}

export const authService = AuthService.getInstance();
